import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { PortalRoutePath, getPortalRoute } from '@models';
import { useLoggedinUser, useService } from '@hooks';
import {
	authService,
	bulkFleetVehicleUploadService,
	CreateUser,
	loadingSpinnerOverlayService,
	notificationService,
	Organization,
	organizationService,
	ResetPasswordRequest,
	userService
} from '@services';
import { RxUtils, Tr } from '@utils';
import { Button, Stack } from '@mui/material';
import { EntityDeleteConfirmationDialog, RouterLinkButton } from '@components/common';
import { EmptyEntityPageLayout, PortalPageTitle } from '@components/portal/common';
import { ReactComponent as Illustration } from '@assets/illustrations/no-locations-added.svg';
import {
	AdminResetPasswordDialog,
	OrganizationAssignRoleDialog,
	OrganizationList,
	OrganizationViewDialog
} from '@components/portal/organizations';

const paddingX = 9;
type Entity = Organization;

const VehicleListUploader: FC<{ onUpload: (file: File) => void }> = ({ onUpload }) => {
	const { open, acceptedFiles, getInputProps } = useDropzone({
		noClick: true,
		noKeyboard: true,
		noDrag: true,
		multiple: false,
		accept: {
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xls', '.xlsx'],
			'application/vnd.ms-excel': ['.xls', '.xlsx']
		}
	});

	useEffect(() => {
		open();
	}, []);

	useEffect(() => {
		const [file] = acceptedFiles;
		file && onUpload(file);
	}, [acceptedFiles]);

	return <input {...getInputProps()} style={{ display: 'none ' }} />;
};

export const Organizations: FC = () => {
	const { t } = useTranslation('portal');
	const navigate = useNavigate();
	const user = useLoggedinUser();
	const isAdmin = user?.isAdmin() || false;
	const [searchText, setSearchText] = useState('');
	const [adminResetDialogVisible, setAdminResetDialogVisible] = useState(false);
	const [searchResult, setSearchResult] = useState<Entity[]>([]);
	const [viewingEntity, setViewingEntity] = useState<Entity>();
	const [vehicleListUploadingEntity, setVehicleListUploadingEntity] = useState<Entity>();
	const [assignRoleEntity, setAssignRoleEntity] = useState<Entity>();
	const [deletingEntities, setDeletingEntities] = useState<Entity[]>([]);
	const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
	const { data: allEntities = [], trigger } = useService(() => organizationService.getAll());
	const entities = (searchText ? searchResult : allEntities).sort((a, b) => a.name.localeCompare(b.name));

	const onAssignRole = (s: CreateUser) => {
		return RxUtils.promisify(
			userService.create(s),
			() => (notificationService.sendSuccess(t('organizations.role-assign-success-message')), trigger())
		);
	};
	const onDelete = (s: Entity) => RxUtils.promisify(organizationService.delete(s), trigger);
	const onDeleteMultiple = (s: Entity[]) => RxUtils.promisify(organizationService.deleteMultiple(s), trigger);
	const onAdminResetPassword = (request: ResetPasswordRequest) => {
		return RxUtils.promisify(authService.resetPasswordByAdmin(request), () =>
			notificationService.sendSuccess(t('organizations.reset-password-success-message'))
		);
	};
	const onUploadVehicleList = (entity: Entity, file: File) => {
		if (file) {
			loadingSpinnerOverlayService.increment();
			const formData = new FormData();
			formData.append('file', file, file.name);
			RxUtils.promisify(
				bulkFleetVehicleUploadService.uploadFileByAdmin(entity.id, formData),
				() => loadingSpinnerOverlayService.reset(() => setVehicleListUploadingEntity(undefined)),
				() => loadingSpinnerOverlayService.reset(() => setVehicleListUploadingEntity(undefined))
			);
		}
	};

	useEffect(() => {
		if (searchText) {
			RxUtils.promisify(organizationService.search(searchText), setSearchResult);
		} else {
			setSearchResult([]);
		}
	}, [searchText]);

	return (
		<>
			{Array.isArray(allEntities) && allEntities.length ? (
				<Stack direction="column" spacing={3} sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5 }}>
					<PortalPageTitle
						paddingX={paddingX}
						title={<Tr.Portal path="organizations.title" />}
						subtitle={
							<Tr.Portal
								path={allEntities.length > 1 ? 'organizations.subtitle-plural' : 'organizations.subtitle'}
								options={{ length: allEntities.length }}
							/>
						}
						displaySearchbox
						searchboxProps={{
							testId: 'organization-search-box',
							placeholder: t('organizations.search-placeholder'),
							searchText,
							onSearchTextChange: setSearchText
						}}
					/>
					<OrganizationList
						showResultCount={!!searchText}
						paddingX={paddingX}
						entities={entities}
						selected={selectedEntities}
						onSelectChange={setSelectedEntities}
						onView={setViewingEntity}
						onEdit={({ id }) => navigate(`${getPortalRoute(PortalRoutePath.ORGANIZATIONS)}/${id}`)}
						onAssignRole={setAssignRoleEntity}
						onUploadVehicleList={setVehicleListUploadingEntity}
						onDelete={(entity) => setDeletingEntities([entity])}
					/>

					<Stack direction="row" spacing={3} justifyContent="center" sx={{ paddingX }}>
						{selectedEntities?.length ? (
							<>
								<Button
									data-testid="delete-organization-modal-open-button"
									variant="contained"
									color="secondary"
									sx={{ textTransform: 'uppercase' }}
									onClick={() => setDeletingEntities(selectedEntities)}
								>
									<Tr.Portal path="delete" />
								</Button>
								<Button
									data-testid="cancel-organization-selection-button"
									variant="outlined"
									color="secondary"
									sx={{ textTransform: 'uppercase' }}
									onClick={() => setSelectedEntities([])}
								>
									<Tr.Portal path="cancel" />
								</Button>
							</>
						) : null}
						{!selectedEntities?.length ? (
							<>
								<RouterLinkButton
									data-testid="add-organization-modal-open-button"
									variant="contained"
									sx={{ textTransform: 'uppercase' }}
									href={`${getPortalRoute(PortalRoutePath.ORGANIZATIONS)}/add`}
								>
									<Tr.Portal path="organizations.add-organization" />
								</RouterLinkButton>
								{isAdmin ? (
									<Button
										data-testid="admin-reset-password-modal-open-button"
										variant="outlined"
										color="secondary"
										sx={{ textTransform: 'uppercase' }}
										onClick={() => setAdminResetDialogVisible(true)}
									>
										<Tr.Portal path="organizations.reset-password" />
									</Button>
								) : null}
							</>
						) : null}
					</Stack>
				</Stack>
			) : (
				<EmptyEntityPageLayout
					illustration={<Illustration />}
					title={<Tr.Portal path="organizations.no-organization-added-title" />}
					addButtonText={<Tr.Portal path="organizations.add-organization" />}
					onAddButtonClick={() => navigate(`${getPortalRoute(PortalRoutePath.ORGANIZATIONS)}/add`)}
					addButtonProps={{ 'data-testid': 'add-organization-modal-open-button' }}
				/>
			)}
			<OrganizationViewDialog open={!!viewingEntity} entity={viewingEntity} onClose={() => setViewingEntity(undefined)} />
			<AdminResetPasswordDialog
				open={adminResetDialogVisible}
				onClose={() => setAdminResetDialogVisible(false)}
				onPrimaryButtonClick={(data) => (setAdminResetDialogVisible(false), onAdminResetPassword?.(data))}
			/>
			<OrganizationAssignRoleDialog
				open={!!assignRoleEntity}
				entity={assignRoleEntity}
				onClose={() => setAssignRoleEntity(undefined)}
				onPrimaryButtonClick={(data) => (setAssignRoleEntity(undefined), onAssignRole?.(data))}
			/>
			<EntityDeleteConfirmationDialog
				open={!!deletingEntities?.length}
				content={<Tr.Portal path="organizations.delete-confirmation-message" />}
				buttonProps={{ 'data-testid': 'organization-delete-confirmation-button' }}
				onClose={() => setDeletingEntities([])}
				onAccept={() => {
					deletingEntities.length > 1 ? onDeleteMultiple?.(deletingEntities) : onDelete?.(deletingEntities[0]);
					setDeletingEntities([]);
					setSelectedEntities([]);
				}}
			>
				{deletingEntities.map((e) => `"${e.name}"`).join(', ')}
			</EntityDeleteConfirmationDialog>
			{vehicleListUploadingEntity ? (
				<VehicleListUploader onUpload={(file) => onUploadVehicleList(vehicleListUploadingEntity, file)} />
			) : null}
		</>
	);
};
